import type {
  LlmPromptsBundle,
  LlmPromptsResponse,
} from "@/shared/services/llm-prompts";
import { llmPromptsApi } from "@/shared/services/llm-prompts";
import { copyToClipboardText } from "@/shared/utils/copy-to-clipboard-text";
import Button from "@components/ui/button";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { IconCopy, IconRotateClockwise2, IconX } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { LLM_PROMPTS_DIALOG_ID } from "../constants";

// Sections + per-section keys + UI labels. Mirrors `ALLOWED_PROMPT_KEYS` in
// dotell-backend/assets-deployment/service.js — keep in sync if either side
// adds/removes a key.
export const SECTIONS: Array<{
  key: keyof LlmPromptsBundle;
  title: string;
  description: string;
  fields: Array<{
    key: string;
    label: string;
    rows: number;
  }>;
}> = [
  {
    key: "promptOnly",
    title: "Prompt-only",
    description:
      "Used when the user submits a free-text prompt instead of a PDF.",
    fields: [
      { key: "roleInstructions", label: "Role instructions", rows: 12 },
      {
        key: "reasoningInstructions",
        label: "Reasoning instructions",
        rows: 5,
      },
      { key: "outputFormat", label: "Output format", rows: 4 },
    ],
  },
  {
    key: "deckOverview",
    title: "Deck overview (PDF)",
    description:
      "First vision pass over the full PDF — produces planning hints.",
    fields: [{ key: "systemPrompt", label: "System prompt", rows: 12 }],
  },
  {
    key: "imageAssignment",
    title: "Image assignment (PDF)",
    description:
      "Maps each extracted PDF image to a slide group/role for slide generation.",
    fields: [{ key: "systemPrompt", label: "System prompt", rows: 12 }],
  },
  {
    key: "slideGeneration",
    title: "Slide generation (PDF)",
    description:
      "Per-batch slide generation. The biggest prompt — drives output quality.",
    fields: [
      { key: "pdfRoleInstructions", label: "PDF role instructions", rows: 16 },
      {
        key: "pdfReasoningInstructions",
        label: "PDF reasoning instructions",
        rows: 6,
      },
      { key: "pdfOutputFormat", label: "PDF output format", rows: 4 },
    ],
  },
  {
    key: "classifyImages",
    title: "Classify images (PDF)",
    description: "Per-image vision classification (Haiku).",
    fields: [{ key: "prompt", label: "Prompt", rows: 8 }],
  },
  {
    key: "pageBackground",
    title: "Page background (PDF)",
    description:
      "Per-page background classification (dark/light + full-bleed) that steers layout and logo-inversion choices.",
    fields: [{ key: "prompt", label: "Prompt", rows: 8 }],
  },
  {
    key: "detectRegions",
    title: "Detect regions (PDF)",
    description:
      "Vision pass that finds chart/diagram/image/logo regions on each page. An image-dimensions suffix is always appended automatically — don't restate sizes here.",
    fields: [{ key: "prompt", label: "Prompt", rows: 12 }],
  },
  {
    key: "chartExtraction",
    title: "Chart extraction (PDF)",
    description:
      "Extracts structured chart data (type, labels, series values) from cropped chart regions.",
    fields: [{ key: "prompt", label: "Prompt", rows: 12 }],
  },
  {
    key: "tableExtraction",
    title: "Table extraction (PDF)",
    description:
      "Extracts a data table from a cropped table region into a clean rectangular grid.",
    fields: [{ key: "prompt", label: "Prompt", rows: 12 }],
  },
  {
    key: "layoutPick",
    title: "Layout pick (template mode)",
    description:
      "Assigns each PDF page to the best-fitting approved layout from the fixed menu. The menu itself is always appended automatically — don't restate layout ids here.",
    fields: [{ key: "systemPrompt", label: "System prompt", rows: 14 }],
  },
  {
    key: "slotFill",
    title: "Slot fill (template mode)",
    description:
      "Fills each chosen layout's slots with content transcribed from the PDF page. The biggest template-mode prompt — drives fill quality (copy-only fidelity, elastic text, secondary lines). Per-page slot lists are appended automatically.",
    fields: [{ key: "systemPrompt", label: "System prompt", rows: 16 }],
  },
  {
    key: "layoutSkeleton",
    title: "Layout skeleton contract (template mode)",
    description:
      "The contract text that forces slide generation to build each page on its approved layout skeleton. Per-page skeleton JSON blocks are appended automatically.",
    fields: [{ key: "contract", label: "Contract", rows: 10 }],
  },
  {
    key: "redesign",
    title: "Slide redesign (editor)",
    description:
      "Decides how \"Redesign this slide\" changes a slide: keep the layout, a section option (cards, timeline, tabs…) or a library layout. Not part of the PDF import. The slide summary, deck style and the menu of options are added automatically; the answer must stay the same JSON object. The compose prompt writes the shortened cards for \"Tighten into cards\".",
    fields: [
      { key: "systemPrompt", label: "System prompt", rows: 16 },
      { key: "composePrompt", label: "Compose prompt (Tighten into cards)", rows: 12 },
    ],
  },
];

type FormState = Record<string, Record<string, string>>;

const buildInitialState = (response: LlmPromptsResponse | null): FormState => {
  const out: FormState = {};
  for (const section of SECTIONS) {
    const sectionKey = section.key as string;
    out[sectionKey] = {};
    for (const field of section.fields) {
      const override = response?.current?.[section.key]?.[
        field.key as keyof LlmPromptsBundle[typeof section.key]
      ] as string | undefined;
      const fallback = response?.defaults?.[section.key]?.[
        field.key as keyof LlmPromptsBundle[typeof section.key]
      ] as string | undefined;
      out[sectionKey][field.key] = override ?? fallback ?? "";
    }
  }
  return out;
};

const buildSavePayload = (
  values: FormState,
  defaults: LlmPromptsResponse["defaults"] | null,
): LlmPromptsBundle => {
  // Sparse override: only include values that differ from the bundled
  // default. Keeps the GCS JSON small and makes diffs readable.
  const out: LlmPromptsBundle = {};
  for (const section of SECTIONS) {
    const sectionValues = values[section.key as string] || {};
    const sectionDefaults = defaults?.[section.key];
    const overridden: Record<string, string> = {};
    for (const field of section.fields) {
      const v = sectionValues[field.key];
      if (typeof v !== "string" || v.length === 0) continue;
      const def = sectionDefaults
        ? (sectionDefaults[field.key as keyof typeof sectionDefaults] as
            | string
            | undefined)
        : undefined;
      if (def !== undefined && v === def) continue;
      overridden[field.key] = v;
    }
    if (Object.keys(overridden).length > 0) {
      (out as Record<string, unknown>)[section.key as string] = overridden;
    }
  }
  return out;
};

const LlmPromptsEditor = () => {
  const dispatch = useAppDispatch();
  const [response, setResponse] = useState<LlmPromptsResponse | null>(null);
  const [values, setValues] = useState<FormState>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setLoadError(null);
    llmPromptsApi
      .get(controller.signal)
      .then((data) => {
        setResponse(data);
        setValues(buildInitialState(data));
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        const msg =
          (err as Error)?.message || "Failed to load prompts from the backend.";
        setLoadError(msg);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, []);

  const close = () => dispatch(closeDialog(LLM_PROMPTS_DIALOG_ID));

  const handleFieldChange = (
    section: string,
    field: string,
    nextValue: string,
  ) => {
    setValues((prev) => ({
      ...prev,
      [section]: { ...(prev[section] || {}), [field]: nextValue },
    }));
  };

  const handleCopy = async (text: string) => {
    await copyToClipboardText({ text, successMessage: "Copied to clipboard" });
  };

  const handleResetField = (section: string, field: string) => {
    if (!response) return;
    const sectionDefaults = response.defaults[
      section as keyof LlmPromptsResponse["defaults"]
    ] as Record<string, string> | undefined;
    const def = sectionDefaults?.[field];
    if (typeof def !== "string") return;
    handleFieldChange(section, field, def);
  };

  // Force every field back to its bundled default, unconditionally — unlike the
  // per-field Reset (disabled once a field already equals its default), this
  // always works. Saving afterwards writes an empty override set, so the
  // pipeline falls back to bundled defaults everywhere.
  const handleResetAll = () => {
    if (!response) return;
    const next: FormState = {};
    for (const section of SECTIONS) {
      const sectionKey = section.key as string;
      next[sectionKey] = {};
      const sectionDefaults = response.defaults[section.key] as
        | Record<string, string>
        | undefined;
      for (const field of section.fields) {
        next[sectionKey][field.key] = sectionDefaults?.[field.key] ?? "";
      }
    }
    setValues(next);
  };

  const handleSave = async () => {
    if (!response || isSaving) return;
    const payload = buildSavePayload(values, response.defaults);
    try {
      setIsSaving(true);
      await llmPromptsApi.update(payload);
      const overrideCount = Object.values(payload).reduce(
        (acc, section) =>
          acc + Object.keys(section as Record<string, string>).length,
        0,
      );
      toast.success(
        overrideCount === 0
          ? "Saved - no overrides, pipeline will use bundled defaults."
          : `Saved ${overrideCount} override${overrideCount === 1 ? "" : "s"} to GCS.`,
      );
      close();
    } catch (err) {
      const msg = (err as Error)?.message || "Failed to save prompts.";
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const overrideSummary = useMemo(() => {
    if (!response) return null;
    const payload = buildSavePayload(values, response.defaults);
    const count = Object.values(payload).reduce(
      (acc, section) =>
        acc + Object.keys(section as Record<string, string>).length,
      0,
    );
    return count;
  }, [values, response]);

  return (
    <div className="tw-flex tw-w-[860px] tw-max-w-[90vw] tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-blue/5 tw-bg-white tw-shadow-sm">
      <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-b-blue/5 tw-px-6 tw-py-4">
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <h2 className="tw-m-0 tw-font-sans tw-text-base tw-font-semibold tw-leading-6 tw-text-blue">
            Edit LLM prompts
          </h2>
          <p className="tw-m-0 tw-font-sans tw-text-xs tw-leading-4 tw-text-blue-500">
            Overrides are stored in GCS and fetched on every generation job.
            Empty fields fall back to the bundled defaults.
          </p>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={close}
          className="tw-flex tw-size-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-text-blue-500 tw-transition-colors hover:tw-bg-blue-50 hover:tw-text-blue"
        >
          <IconX className="tw-size-4" />
        </button>
      </div>

      <div className="scrollbar tw-flex tw-max-h-[70vh] tw-flex-col tw-gap-8 tw-overflow-y-auto tw-px-6 tw-py-6">
        {isLoading && (
          <p className="tw-m-0 tw-font-sans tw-text-sm tw-text-blue-500">
            Loading current prompts…
          </p>
        )}
        {loadError && (
          <p className="tw-m-0 tw-rounded-md tw-bg-red-50 tw-px-3 tw-py-2 tw-font-sans tw-text-sm tw-text-[#dc2626]">
            {loadError}
          </p>
        )}
        {!isLoading &&
          !loadError &&
          response &&
          SECTIONS.map((section) => (
            <section
              key={section.key as string}
              className="tw-flex tw-flex-col tw-gap-4"
            >
              <div className="tw-flex tw-flex-col tw-gap-0.5">
                <h3 className="tw-m-0 tw-font-sans tw-text-sm tw-font-semibold tw-leading-5 tw-text-blue">
                  {section.title}
                </h3>
                <p className="tw-m-0 tw-font-sans tw-text-xs tw-leading-4 tw-text-blue-500">
                  {section.description}
                </p>
              </div>
              {section.fields.map((field) => {
                const sectionKey = section.key as string;
                const value = values[sectionKey]?.[field.key] ?? "";
                const sectionDefaults = response.defaults[section.key] as
                  | Record<string, string>
                  | undefined;
                const def = sectionDefaults?.[field.key] ?? "";
                const isModifiedFromDefault = value !== def;
                return (
                  <div
                    key={field.key}
                    className="tw-flex tw-flex-col tw-gap-1.5"
                  >
                    <div className="tw-flex tw-items-center tw-justify-between">
                      <label
                        htmlFor={`prompt-${sectionKey}-${field.key}`}
                        className="tw-font-sans tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider tw-text-blue-500"
                      >
                        {field.label}
                        {isModifiedFromDefault && (
                          <span className="tw-ml-2 tw-rounded tw-bg-orange-100 tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-text-orange-700">
                            Overridden
                          </span>
                        )}
                      </label>
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <button
                          type="button"
                          onClick={() => handleCopy(value)}
                          className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-md tw-border tw-border-solid tw-border-blue/10 tw-bg-white tw-px-2 tw-py-1 tw-font-sans tw-text-xs tw-text-blue-500 tw-transition-colors hover:tw-bg-blue-50"
                        >
                          <IconCopy className="tw-size-3.5" />
                          Copy
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            handleResetField(sectionKey, field.key)
                          }
                          disabled={!isModifiedFromDefault}
                          className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-md tw-border tw-border-solid tw-border-blue/10 tw-bg-white tw-px-2 tw-py-1 tw-font-sans tw-text-xs tw-text-blue-500 tw-transition-colors hover:tw-bg-blue-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 disabled:hover:tw-bg-white"
                        >
                          <IconRotateClockwise2 className="tw-size-3.5" />
                          Reset to default
                        </button>
                      </div>
                    </div>
                    <textarea
                      id={`prompt-${sectionKey}-${field.key}`}
                      value={value}
                      rows={field.rows}
                      onChange={(event) =>
                        handleFieldChange(
                          sectionKey,
                          field.key,
                          event.target.value,
                        )
                      }
                      spellCheck={false}
                      className="tw-w-full tw-resize-y tw-rounded-md tw-border tw-border-solid tw-border-blue/10 tw-bg-white tw-px-3 tw-py-2 tw-font-mono tw-text-xs tw-leading-5 tw-text-blue tw-outline-none focus:tw-border-blue/30"
                    />
                  </div>
                );
              })}
            </section>
          ))}
      </div>

      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-t tw-border-t-blue/5 tw-px-6 tw-py-3">
        <p className="tw-m-0 tw-font-sans tw-text-xs tw-text-blue-500">
          {overrideSummary === null
            ? null
            : overrideSummary === 0
              ? "No overrides — saving will clear all overrides in GCS."
              : `${overrideSummary} override${overrideSummary === 1 ? "" : "s"} ready to save.`}
        </p>
        <div className="tw-flex tw-items-center tw-gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleResetAll}
            disabled={isLoading || isSaving || !!loadError || overrideSummary === 0}
          >
            Reset all to defaults
          </Button>
          <Button variant="ghost" size="sm" onClick={close} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={isLoading || isSaving || !!loadError}
            isLoading={isSaving}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LlmPromptsEditor;
